import { Badge, type BadgeTone } from '@chopchop-se/ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Status pills for the module's four status vocabularies.
 *
 * The tone mapping is the thing worth being deliberate about: a warehouse
 * screen is read at three metres, so "something needs attention" (brist,
 * avbokad) must be red, "in flight" amber, and "done" green — in every list,
 * identically.
 */

const ROUND_TONES: Record<string, BadgeTone> = {
  planned: 'neutral',
  picking: 'warning',
  picked: 'success',
  packed: 'brand',
  closed: 'neutral',
};

const TASK_TONES: Record<string, BadgeTone> = {
  open: 'neutral',
  claimed: 'warning',
  picked: 'success',
  short: 'danger',
};

const SHIPMENT_TONES: Record<string, BadgeTone> = {
  draft: 'neutral',
  booked: 'info',
  shipped: 'brand',
  delivered: 'success',
  cancelled: 'danger',
};

export function RoundStatusBadge({ status }: { status: string }): ReactNode {
  const { t } = useTranslation('pickingShipping');
  return (
    <Badge tone={ROUND_TONES[status] ?? 'neutral'} dot minWidth>
      {t(`rounds.status.${status}`, { defaultValue: status })}
    </Badge>
  );
}

export function TaskStatusBadge({ status }: { status: string }): ReactNode {
  const { t } = useTranslation('pickingShipping');
  return (
    <Badge tone={TASK_TONES[status] ?? 'neutral'} dot minWidth>
      {t(`tasks.status.${status}`, { defaultValue: status })}
    </Badge>
  );
}

export function ShipmentStatusBadge({ status }: { status: string }): ReactNode {
  const { t } = useTranslation('pickingShipping');
  return (
    <Badge tone={SHIPMENT_TONES[status] ?? 'neutral'} dot minWidth>
      {t(`shipments.status.${status}`, { defaultValue: status })}
    </Badge>
  );
}

export function ModeBadge({ mode }: { mode: string }): ReactNode {
  const { t } = useTranslation('pickingShipping');
  return (
    <Badge tone={mode === 'paper' ? 'accent' : 'brand'} size="sm">
      {t(`rounds.modes.${mode}`, { defaultValue: mode })}
    </Badge>
  );
}
